import React, { useEffect, useState } from "react";
import { api, brl } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarClock, MessageCircle, AlertTriangle, XOctagon, Rocket } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function MeuPlano() {
  const [data, setData] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [adhering, setAdhering] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/my-plan", { _cache: false });
      setData(r.data);
    } catch (e) {
      setData({ error: e.response?.data?.detail || "Erro ao carregar plano" });
    }
  };
  useEffect(() => { load(); }, []);

  const cancel = async () => {
    setCancelling(true);
    try {
      const r = await api.post("/tenant/subscription/cancel");
      toast.success(r.data?.message || "Assinatura cancelada");
      setCancelOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cancelar");
    } finally { setCancelling(false); }
  };

  // Botão "Aderir plano" — gera pay-link self-service e redireciona pro checkout
  // público (mesma tela usada quando o teste expira). Idempotente.
  const adhere = async () => {
    setAdhering(true);
    try {
      const r = await api.post("/tenant/pay-link");
      const url = r.data?.pay_url;
      if (!url) throw new Error("Link não gerado");
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar link de pagamento");
      setAdhering(false);
    }
  };

  if (!data) {
    return <div className="text-sm text-neutral-500">Carregando…</div>;
  }
  if (data.error) {
    return (
      <div className="space-y-6 max-w-2xl" data-testid="meu-plano-page">
        <div>
          <p className="label-tiny mb-2">Assinatura</p>
          <h1 className="font-display text-4xl tracking-tight">Meu plano</h1>
        </div>
        <Card className="p-8 border-[#E5E5E5] dark:border-white/10 shadow-none text-center">
          <AlertTriangle size={32} className="text-amber-500 mx-auto mb-3"/>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Não foi possível carregar os dados da sua assinatura.
          </p>
          <p className="text-xs text-neutral-400 mt-2">{String(data.error)}</p>
          <p className="text-xs text-neutral-500 mt-4">
            Faça logout e logue novamente. Se o problema persistir, entre em contato com o suporte.
          </p>
        </Card>
      </div>
    );
  }

  const { plan_label, plan_price, final_price, discount, days_left, expired, expires_at, subscription_type, support_whatsapp } = data;
  const hasDiscount = !!(discount && discount.kind);
  const effectivePrice = final_price != null ? final_price : plan_price;
  const noPlan = days_left == null;
  let colorClass = "text-emerald-700 dark:text-emerald-400";
  if (expired) colorClass = "text-red-600";
  else if (days_left != null && days_left <= 3) colorClass = "text-red-600";
  else if (days_left != null && days_left <= 7) colorClass = "text-orange-600";

  const daysLabel = expired
    ? `Expirado há ${Math.abs(days_left)} dia${Math.abs(days_left) === 1 ? "" : "s"}`
    : (noPlan ? "Sem plano ativo" : `${days_left} dia${days_left === 1 ? "" : "s"} restantes`);

  const openWa = () => {
    const phone = (support_whatsapp || "").replace(/\D/g, "");
    if (!phone) return;
    const full = phone.startsWith("55") ? phone : "55" + phone;
    const msg = expired
      ? "Olá! Minha assinatura do Gestão Expert venceu, gostaria de renovar."
      : "Olá! Tudo bem? Gostaria de tirar uma dúvida sobre minha assinatura no Gestão Expert.";
    window.open(`https://wa.me/${full}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-8 max-w-2xl" data-testid="meu-plano-page">
      <div>
        <p className="label-tiny mb-2">Assinatura</p>
        <h1 className="font-display text-4xl tracking-tight">Meu plano</h1>
        <p className="text-sm text-neutral-500 mt-1">Detalhes da sua assinatura no Gestão Expert.</p>
      </div>

      <Card className="p-8 border-[#E5E5E5] dark:border-white/10 shadow-none">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="label-tiny">Plano atual</p>
            <h2 className="font-display text-3xl tracking-tight mt-1">{plan_label || "—"}</h2>
            {plan_price != null && (
              hasDiscount ? (
                <p className="text-sm mt-1">
                  <span className="line-through text-neutral-400 mr-1.5">{brl(plan_price)}</span>
                  <span className="font-semibold text-emerald-700 dark:text-emerald-400">{brl(effectivePrice)}</span>
                  <span className="text-neutral-500"> / mês</span>
                </p>
              ) : (
                <p className="text-sm text-neutral-500 mt-1">{`${brl(plan_price)} / mês`}</p>
              )
            )}
            {hasDiscount && (
              <p className="text-[11px] mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300" data-testid="discount-badge">
                Desconto: {discount.kind === "percent" ? `${discount.value}%` : brl(discount.value)}
                {discount.duration === "months" ? ` · ${discount.months_remaining ?? discount.original_months} mês(es) restante(s)` : " · Permanente"}
              </p>
            )}
          </div>
          {subscription_type === "trial" && <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-1 rounded bg-blue-100 text-blue-800">Em teste</span>}
          {subscription_type === "paid" && <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-1 rounded bg-emerald-100 text-emerald-800">Assinatura</span>}
        </div>

        <div className="mt-8 flex items-center gap-3">
          <CalendarClock size={20} className={colorClass}/>
          <div>
            <div className={`font-display text-2xl tracking-tight ${colorClass}`} data-testid="days-left-display">{daysLabel}</div>
            {expires_at && (
              <div className="text-xs text-neutral-500 mt-1">
                {expired ? "Venceu em" : "Vence em"}: {new Date(expires_at).toLocaleString("pt-BR")}
              </div>
            )}
          </div>
        </div>

        {expired && (
          <div className="mt-6 flex items-start gap-3 p-3 rounded-md bg-amber-50 border border-amber-100 dark:bg-amber-900/10 dark:border-amber-900/30">
            <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5"/>
            <p className="text-xs text-amber-800 dark:text-amber-300">
              Sua assinatura está vencida. Após 2 dias, o acesso ao sistema será bloqueado automaticamente. Renove para continuar.
            </p>
          </div>
        )}

        {/* Botão "Aderir plano" — aparece quando o tenant está em teste OU sem
            assinatura recorrente ativa. Leva pro mesmo checkout que aparece
            quando o teste expira. */}
        {(subscription_type === "trial" || (!data.stripe_subscription_id && subscription_type !== "paid")) && (
          <div className="mt-6 pt-5 border-t border-neutral-200 dark:border-white/10" data-testid="adhere-plan-section">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="label-tiny">Contratar agora</p>
                <p className="text-sm text-neutral-500 mt-2">
                  Aproveite o mesmo checkout seguro do Stripe: escolha pagar por PIX ou assinar mensal no cartão recorrente.
                </p>
              </div>
              <Button
                onClick={adhere}
                disabled={adhering}
                className="bg-emerald-500 hover:bg-emerald-600 text-white shrink-0"
                data-testid="adhere-plan-btn"
              >
                <Rocket size={14} className="mr-1.5"/>
                {adhering ? "Abrindo checkout…" : "Aderir plano"}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {data && data.stripe_subscription_id && (
        <Card className="p-6 border-[#E5E5E5] dark:border-white/10 shadow-none" data-testid="stripe-sub-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="label-tiny">Assinatura recorrente Stripe</p>
              <p className="text-sm text-neutral-500 mt-2">
                {data.stripe_cancel_at_period_end
                  ? "Sua assinatura foi cancelada. Você continuará com acesso até a data de expiração acima e depois não será mais cobrado."
                  : "Você tem uma assinatura ativa. Ao cancelar, os próximos ciclos deixam de ser cobrados, mas você continua com acesso até a data de expiração."}
              </p>
            </div>
            {!data.stripe_cancel_at_period_end && (
              <Button onClick={()=>setCancelOpen(true)} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="cancel-subscription-btn">
                <XOctagon size={14} className="mr-1.5"/> Cancelar assinatura
              </Button>
            )}
          </div>
        </Card>
      )}

      {support_whatsapp && (
        <Card className="p-6 border-[#E5E5E5] dark:border-white/10 shadow-none">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="label-tiny">Suporte / Renovação</p>
              <p className="text-sm text-neutral-500 mt-2">Precisa renovar, tirar dúvidas ou pedir ajuda? Fale com a gente direto no WhatsApp.</p>
            </div>
            <Button onClick={openWa} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="contact-whatsapp-button">
              <MessageCircle size={14} className="mr-1.5"/> Falar no WhatsApp
            </Button>
          </div>
        </Card>
      )}

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="cancel-sub-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar assinatura?</AlertDialogTitle>
            <AlertDialogDescription>
              Ao confirmar, o Stripe deixa de cobrar novos ciclos.
              <br/>Você <strong>continua com acesso ao sistema</strong> até a data de expiração exibida acima. Sua conta e todos os dados permanecem íntegros.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e)=>{ e.preventDefault(); cancel(); }}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-700"
              data-testid="cancel-sub-confirm">
              {cancelling ? "Cancelando…" : "Sim, cancelar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
